import { useState, useEffect } from "react";

const imageCache = new Map<string, HTMLImageElement>();

export interface UseImageCacheOptions {
  fallbackUrl?: string;
  suspense?: boolean;
}

export function useImageCache(
  url: string,
  options?: UseImageCacheOptions
): { image: HTMLImageElement | null; isLoading: boolean; error: Error | null } {
  const { fallbackUrl, suspense } = options || {};
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (!url) {
      setIsLoading(false);
      return;
    }

    // Se já estiver no cache, usa imediatamente
    if (imageCache.has(url)) {
      setImage(imageCache.get(url)!);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    // Carrega a imagem via fetch para permitir abort
    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error("Falha ao buscar imagem");
        return response.blob();
      })
      .then((blob) => {
        if (!isMounted) return;
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          imageCache.set(url, img);
          if (isMounted) {
            setImage(img);
            setIsLoading(false);
          }
          URL.revokeObjectURL(blobUrl);
        };
        img.onerror = () => {
          // Tenta carregar fallback, se definido
          if (fallbackUrl) {
            loadFallback(fallbackUrl);
          } else if (isMounted) {
            setError(new Error(`Falha ao carregar imagem: ${url}`));
            setIsLoading(false);
          }
        };
        img.src = blobUrl;
      })
      .catch((err: any) => {
        if (err.name === "AbortError") return;
        // Se ocorrer erro, tenta fallback (se definido)
        if (fallbackUrl) {
          loadFallback(fallbackUrl);
        } else if (isMounted) {
          setError(new Error(`Falha ao buscar imagem: ${url}`));
          setIsLoading(false);
        }
      });

    const loadFallback = (fbUrl: string) => {
      fetch(fbUrl, { signal })
        .then((resp) => {
          if (!resp.ok) throw new Error("Falha ao buscar fallback");
          return resp.blob();
        })
        .then((blob) => {
          if (!isMounted) return;
          const blobUrl = URL.createObjectURL(blob);
          const fbImg = new Image();
          fbImg.crossOrigin = "anonymous";
          fbImg.onload = () => {
            imageCache.set(url, fbImg); // armazena fallback no cache
            if (isMounted) {
              setImage(fbImg);
              setIsLoading(false);
            }
            URL.revokeObjectURL(blobUrl);
          };
          fbImg.onerror = () => {
            if (isMounted) {
              setError(new Error(`Falha ao carregar fallback: ${fbUrl}`));
              setIsLoading(false);
            }
          };
          fbImg.src = blobUrl;
        })
        .catch((err: any) => {
          if (err.name === "AbortError") return;
          if (isMounted) {
            setError(new Error(`Falha ao buscar fallback: ${fbUrl}`));
            setIsLoading(false);
          }
        });
    };

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [url, fallbackUrl]);

  // Se estiver usando Suspense, lança uma promise enquanto carrega
  if (suspense && isLoading) {
    throw new Promise(() => {});
  }

  return { image, isLoading, error };
}
